import { useCallback, useRef, useState } from "react";
import { auth } from "../lib/firebase";
import { authenticatedFetch } from "../lib/api";
import { TenantDashboard } from "../types";

/** What the Overview asks the server for (ENG-2618). */
export interface OverviewQuery {
  page: number;
  pageSize: number;
  /** Searches member name + email, server-side. Pass the debounced value, not every keystroke. */
  q?: string;
  /** Scope the whole report to one group. Null = the whole tenant. */
  groupId?: string | null;
  /**
   * ENG-3357: bumped by the console after every membership change. Part of the cache key and never
   * sent, so a dashboard loaded before an invite, removal or approval is not reused after it.
   */
  rosterRevision?: number;
}

// Tenant Overview dashboard data hook (ENG-2429). Admin-only: fetches the caller's own tenant
// dashboard from GET /business/dashboard (tenant resolved server-side from the Firebase token).
// Fails honest - on error it surfaces a message and clears the report rather than fabricating data.
//
// The response carries one page of the roster plus server-computed aggregates, so every control that
// changes what is shown (page, page size, search, group filter) is a refetch. `overviewLoaded` means
// the first load succeeded, which is what lets the page tell a refetch (keep the old rows up) from a
// cold load (spinner).
//
export function useOverview() {
  const [dashboard, setDashboard] = useState<TenantDashboard | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [overviewLoaded, setOverviewLoaded] = useState(false);
  // ENG-3350: the group filter the server just refused with a 404 — a group that no longer exists
  // (deleted in another tab, or by another admin). The page clears the filter when it sees this.
  const [overviewMissingGroup, setOverviewMissingGroup] = useState<string | null>(null);
  // Monotonic request id: a slower earlier response must not land after a newer one and win.
  const latestRequest = useRef(0);
  // ENG-2963: the (page, pageSize, q, groupId) this dashboard was last loaded for. Mirrors
  // useRiskReport's loadedKey (and useTelemetryEvents') — a re-mount's fetch is a no-op when
  // nothing changed, instead of re-hitting the BigQuery-backed dashboard behind a spinner every
  // time the admin navigates back to Overview. A ref, not state, for the same reason as
  // useRiskReport: `fetchOverview` is a stable useCallback that OverviewPage depends on
  // directly (its load effect is keyed on `onFetch`), so state here would force a new identity on
  // every load and re-trigger that effect. Set only on success, so a failed fetch retries.
  const loadedKey = useRef<string | null>(null);
  // ENG-3527: bumped by `invalidate`, which the org-policy notifier calls after every policy write
  // (useOrgPolicyNotifier). Part of the cache key, as `rosterRevision` is for membership: the
  // dashboard carries the enforcement mode and what the allow list, deny list and flags do, and a
  // cached one went on saying Monitor-Only after the admin had switched to Active Enforcement.
  //
  // State, not a ref, so `fetchOverview` takes a new identity when it moves — and only then, not on
  // every load. The Overview's load effect is keyed on it, so an Overview on screen when the policy
  // changes asks again, and one visited later misses the cache.
  const [policyRevision, setPolicyRevision] = useState(0);

  // Stable identity between policy changes: the Overview drives this from an effect keyed on the
  // paging state, so a new function every render would refetch forever.
  const fetchOverview = useCallback(async (query: OverviewQuery) => {
    if (!auth.currentUser) return;
    const key = `${query.page}|${query.pageSize}|${query.q?.trim() ?? ""}|${query.groupId ?? ""}|${query.rosterRevision ?? 0}|${policyRevision}`;
    if (loadedKey.current === key) return; // already have this exact query - no spinner, no round-trip
    const seq = ++latestRequest.current;
    setLoadingOverview(true);
    setOverviewError(null);
    setOverviewMissingGroup(null);
    try {
      const params = new URLSearchParams({ page: String(query.page), pageSize: String(query.pageSize) });
      if (query.q?.trim()) params.set("q", query.q.trim());
      if (query.groupId) params.set("groupId", query.groupId);
      const response = await authenticatedFetch(`/business/dashboard?${params}`);
      const body = response.ok ? await response.json() : null;
      if (seq !== latestRequest.current) return; // superseded while in flight
      if (response.ok) {
        setDashboard(body);
        setOverviewLoaded(true);
        loadedKey.current = key;
      } else {
        // A 404 means the group filter names a group that is gone (deleted in another tab, or a stale
        // stored selection). Say that, rather than blaming the whole dashboard for it.
        setDashboard(null);
        loadedKey.current = null;
        const groupGone = response.status === 404 && !!query.groupId;
        if (groupGone) setOverviewMissingGroup(query.groupId ?? null);
        setOverviewError(groupGone
          ? "That group no longer exists. Clear the filter to see the whole organization."
          : "Couldn't load your organization's overview.");
      }
    } catch (err) {
      console.error("Error fetching tenant dashboard:", err);
      setDashboard(null);
      loadedKey.current = null;
      setOverviewError("Couldn't load your organization's overview.");
    } finally {
      if (seq === latestRequest.current) setLoadingOverview(false);
    }
  }, [policyRevision]);

  /** ENG-3527: the organization's policy changed; the next fetch goes to the server. */
  const invalidate = useCallback(() => setPolicyRevision((r) => r + 1), []);

  const reset = () => {
    // Retire any in-flight request: without this a rebuild dispatched before a sign-out still lands
    // afterwards and renders the previous account's roster to the next one.
    latestRequest.current++;
    setDashboard(null);
    setOverviewError(null);
    setOverviewMissingGroup(null);
    setOverviewLoaded(false);
    setLoadingOverview(false);
    loadedKey.current = null;
  };

  return { dashboard, loadingOverview, overviewError, overviewMissingGroup, overviewLoaded, fetchOverview, invalidate, reset };
}
